import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "en" | "es";

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    dashboard: "Dashboard",
    clients: "Clients",
    jobs: "Jobs",
    estimates: "Estimates",
    invoices: "Invoices",
    expenses: "Expenses",
    vendors: "Vendors",
    savedItems: "Saved Items",
    reports: "Reports",
    profitability: "Profitability",
    team: "Team",
    funnels: "Funnels",
    settings: "Settings",
    
    // Common
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    search: "Search",
    loading: "Loading",
    noData: "No data available",
    success: "Success",
    error: "Error",
    
    // Dashboard
    totalRevenue: "Total Revenue",
    totalExpenses: "Total Expenses",
    netProfit: "Net Profit",
    activeClients: "Active Clients",
    activeJobs: "Active Jobs",
    pendingInvoices: "Pending Invoices",
    totalEstimates: "Total Estimates",
    totalVendors: "Total Vendors",
    teamMembers: "Team Members",
    
    // Team
    teamManagement: "Team Management",
    teamManagementDesc: "Manage your team members and their access levels",
    inviteTeamMember: "Invite Team Member",
    inviteTeamMemberDesc: "Add a new member to your organization",
    email: "Email",
    role: "Role",
    invite: "Invite",
    inviteSent: "Invitation sent",
    invitationSentTo: "Invitation sent to",
    currentMembers: "Team Members",
    currentMembersDesc: "Current members of your organization",
    joined: "Joined",
    actions: "Actions",
    member: "Member",
    admin: "Admin",
    owner: "Owner",
    
    // Expenses
    expensesPage: "Expenses",
    trackExpenses: "Track business expenses and spending",
    addExpense: "Add Expense",
    addNewExpense: "Add New Expense",
    addYourFirstExpense: "Add Your First Expense",
    description: "Description",
    category: "Category",
    amount: "Amount",
    supplies: "Supplies",
    software: "Software",
    marketing: "Marketing",
    travel: "Travel",
    equipment: "Equipment",
    utilities: "Utilities",
    other: "Other",
    customCategory: "Custom Category",
    specifyCategory: "Specify category",
    deleteExpense: "Delete Expense",
    expenseDeleted: "Expense deleted successfully",
    expenseAdded: "Expense added successfully",
    
    // Saved Items
    manageItems: "Manage reusable items for estimates and invoices",
    addItem: "Add Item",
    addNewItem: "Add New Item",
    addYourFirstItem: "Add Your First Item",
    itemName: "Item Name",
    unitPrice: "Unit Price",
    itemCreated: "Item created successfully",
    itemUpdated: "Item updated successfully",
    itemDeleted: "Item deleted successfully",
    
    // Settings
    settingsTitle: "Settings",
    settingsDesc: "Manage your account and application preferences",
    languageLabel: "Language",
    selectLanguage: "Select Language",
    english: "English",
    spanish: "Spanish",
    dashboardWidgets: "Dashboard Widgets",
    dashboardWidgetsDesc: "Customize which widgets appear on your dashboard",
  },
  es: {
    // Navegación
    dashboard: "Panel",
    clients: "Clientes",
    jobs: "Trabajos",
    estimates: "Presupuestos",
    invoices: "Facturas",
    expenses: "Gastos",
    vendors: "Proveedores",
    savedItems: "Artículos Guardados",
    reports: "Informes",
    profitability: "Rentabilidad",
    team: "Equipo",
    funnels: "Embudos",
    settings: "Configuración",
    
    // Común
    add: "Agregar",
    edit: "Editar",
    delete: "Eliminar",
    save: "Guardar",
    cancel: "Cancelar",
    search: "Buscar",
    loading: "Cargando",
    noData: "No hay datos disponibles",
    success: "Éxito",
    error: "Error",
    
    // Panel
    totalRevenue: "Ingresos Totales",
    totalExpenses: "Gastos Totales",
    netProfit: "Beneficio Neto",
    activeClients: "Clientes Activos",
    activeJobs: "Trabajos Activos",
    pendingInvoices: "Facturas Pendientes",
    totalEstimates: "Presupuestos Totales",
    totalVendors: "Proveedores Totales",
    teamMembers: "Miembros del Equipo",
    
    // Equipo
    teamManagement: "Gestión de Equipo",
    teamManagementDesc: "Administra los miembros de tu equipo y sus niveles de acceso",
    inviteTeamMember: "Invitar Miembro",
    inviteTeamMemberDesc: "Agrega un nuevo miembro a tu organización",
    email: "Correo Electrónico",
    role: "Rol",
    invite: "Invitar",
    inviteSent: "Invitación enviada",
    invitationSentTo: "Invitación enviada a",
    currentMembers: "Miembros del Equipo",
    currentMembersDesc: "Miembros actuales de tu organización",
    joined: "Se unió",
    actions: "Acciones",
    member: "Miembro",
    admin: "Administrador",
    owner: "Propietario",
    
    // Gastos
    expensesPage: "Gastos",
    trackExpenses: "Rastrea gastos y gastos comerciales",
    addExpense: "Agregar Gasto",
    addNewExpense: "Agregar Nuevo Gasto",
    addYourFirstExpense: "Agrega Tu Primer Gasto",
    description: "Descripción",
    category: "Categoría",
    amount: "Cantidad",
    supplies: "Suministros",
    software: "Software",
    marketing: "Marketing",
    travel: "Viaje",
    equipment: "Equipo",
    utilities: "Servicios",
    other: "Otro",
    customCategory: "Categoría Personalizada",
    specifyCategory: "Especificar categoría",
    deleteExpense: "Eliminar Gasto",
    expenseDeleted: "Gasto eliminado exitosamente",
    expenseAdded: "Gasto agregado exitosamente",
    
    // Artículos Guardados
    manageItems: "Administra artículos reutilizables para presupuestos y facturas",
    addItem: "Agregar Artículo",
    addNewItem: "Agregar Nuevo Artículo",
    addYourFirstItem: "Agrega Tu Primer Artículo",
    itemName: "Nombre del Artículo",
    unitPrice: "Precio Unitario",
    itemCreated: "Artículo creado exitosamente",
    itemUpdated: "Artículo actualizado exitosamente",
    itemDeleted: "Artículo eliminado exitosamente",
    
    // Configuración
    settingsTitle: "Configuración",
    settingsDesc: "Administra tu cuenta y preferencias de la aplicación",
    languageLabel: "Idioma",
    selectLanguage: "Seleccionar Idioma",
    english: "Inglés",
    spanish: "Español",
    dashboardWidgets: "Widgets del Panel",
    dashboardWidgetsDesc: "Personaliza qué widgets aparecen en tu panel",
  },
};

export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within TranslationProvider");
  }
  return context;
};
